/**
 * Local charging-history cache.
 *
 * Backend does not expose a per-user session listing endpoint today
 * (see PLAN §19). This store records a compact receipt of every
 * session the user finishes on THIS device so History / Session
 * Detail / Dashboard have something to render. Server-side data
 * remains authoritative — SessionDetailPage always re-fetches via
 * getCustomerSession(sessionUid) on mount.
 *
 * Deliberately NOT stored on this device:
 *   - Raw PINs, PIN hashes, or anything derived from a PIN.
 *   - Full card numbers, CVV, or 3DS state.
 *   - Payment secrets, order tokens, correlation IDs.
 *   - Redeem code content beyond the code label the user already saw
 *     in the wizard (which is not a bearer secret — it's atomically
 *     redeemed server-side and single-use per session).
 *
 * Stored per-user, non-sensitive fields only. `paymentLast4` is
 * intentionally NOT stored — a card-last-4 in localStorage is a
 * fingerprinting risk with no dashboard user benefit; the receipt
 * summary re-hydrates it from the backend when the detail page loads.
 */

const KEY_PREFIX = 'gc.user.history.v1.';
const MAX_ENTRIES = 50;
const SCHEMA_VERSION = 1;

function keyFor(userId) {
  return `${KEY_PREFIX}${userId || 'anon'}`;
}

const ALLOWED_FIELDS = [
  'sessionUid', 'stationId', 'connectorId', 'stationName', 'stationLocation',
  'plateRaw', 'plateNormalized', 'billingMode',
  'startedAt', 'endedAt', 'energyKwh', 'averageKw',
  'currency', 'authorizedCents', 'capturedCents',
  'paymentStatus', 'appliedCredit',
  'ratePerKwh', 'sessionFee', 'minChargeAmount',
  'recordedAt',
];

function sanitizeEntry(entry) {
  if (!entry || typeof entry !== 'object' || !entry.sessionUid) return null;
  const out = {};
  for (const f of ALLOWED_FIELDS) {
    if (entry[f] !== undefined) out[f] = entry[f];
  }
  return out;
}

function read(userId) {
  try {
    const raw = localStorage.getItem(keyFor(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.entries)) {
      // Legacy shape (v1 wrote a bare array). Normalise or reject.
      if (Array.isArray(parsed)) return parsed.map(sanitizeEntry).filter(Boolean);
      return [];
    }
    if (parsed.schema !== SCHEMA_VERSION) return [];
    return parsed.entries.map(sanitizeEntry).filter(Boolean);
  } catch { return []; }
}

function write(userId, list) {
  const payload = JSON.stringify({
    schema: SCHEMA_VERSION,
    entries: list.map(sanitizeEntry).filter(Boolean),
  });
  try {
    localStorage.setItem(keyFor(userId), payload);
  } catch (err) {
    if (typeof console !== 'undefined') {
      console.warn('[gc.history] localStorage write failed', err?.name || err);
    }
  }
}

export function listSessions(userId) {
  return read(userId).sort((a, b) => (b.endedAt || b.startedAt || 0) - (a.endedAt || a.startedAt || 0));
}

export function getSession(userId, sessionUid) {
  return read(userId).find((s) => s.sessionUid === sessionUid) || null;
}

/**
 * Add or update a session record. Called at wizard SUMMARY and when
 * SessionDetail refreshes from the backend so we always store the
 * freshest snapshot. Dedupes by sessionUid — repeated SUMMARY renders
 * cannot create duplicate records.
 */
export function upsertSession(userId, record) {
  const clean = sanitizeEntry(record);
  if (!clean) return;
  const list = read(userId);
  const idx = list.findIndex((s) => s.sessionUid === clean.sessionUid);
  const merged = { ...(idx >= 0 ? list[idx] : {}), ...clean };
  const next = idx >= 0
    ? [...list.slice(0, idx), merged, ...list.slice(idx + 1)]
    : [merged, ...list];
  const trimmed = next
    .sort((a, b) => (b.endedAt || b.startedAt || 0) - (a.endedAt || a.startedAt || 0))
    .slice(0, MAX_ENTRIES);
  write(userId, trimmed);
}

export function removeSession(userId, sessionUid) {
  const list = read(userId).filter((s) => s.sessionUid !== sessionUid);
  write(userId, list);
}

export function clearForUser(userId) {
  try { localStorage.removeItem(keyFor(userId)); } catch { /* ignore */ }
}

/** Purge every user's history entry. Called at logout. */
export function clearAllUsers() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(KEY_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => { try { localStorage.removeItem(k); } catch { /* ignore */ } });
  } catch { /* storage disabled */ }
}
